import type { DocumentListItem } from './contract';

/** Internal RHF field — stripped from API payloads by form-engine sanitizer. */
export const DOCUMENT_STORE_DIRTY_FIELD = '__documentStoreDirty';

/** Shared baseline per registry storeKey — all RhfDocumentList instances for one form share this. */
const storeBaselines = new Map<string, string>();

export function getDocumentStoreBaseline(storeKey: string): string | null {
  return storeBaselines.get(storeKey) ?? null;
}

export function clearDocumentStoreBaseline(storeKey: string): void {
  storeBaselines.delete(storeKey);
}

/** Test-only helper. */
export function clearAllDocumentStoreBaselines(): void {
  storeBaselines.clear();
}

/**
 * Stable JSON fingerprint of document-list store items for dirty comparison.
 * Sorts by uniqueDocId so order-only changes do not affect equality.
 */
export function serializeDocumentListItems(items: DocumentListItem[]): string {
  return JSON.stringify(
    [...items]
      .sort((a, b) => a.uniqueDocId.localeCompare(b.uniqueDocId))
      .map(({ uniqueDocId, label, groupKey }) => ({
        uniqueDocId,
        label,
        groupKey: groupKey ?? null,
      })),
  );
}

export interface DocumentStoreDirtySyncInput {
  items: DocumentListItem[];
  formStatus?: string;
  isHydratingRef?: boolean;
  /** Registry store key — resolves shared baseline when local ref is null. */
  storeKey?: string;
  baselineSnapshot: string | null;
  setBaselineSnapshot: (snapshot: string) => void;
}

export interface DocumentStoreDirtySyncResult {
  shouldMarkDirty: boolean;
  /** When true, caller should set dirty field to false without marking form dirty. */
  resetDirtyField: boolean;
}

export interface DocumentStoreBaselineResult {
  established: boolean;
  /**
   * True only when a new post-hydration snapshot was captured.
   * Callers must reset the dirty sentinel only on fresh capture — not when
   * syncing an existing shared baseline to a late-mounting list instance.
   */
  freshCapture: boolean;
}

function isFormHydrating(formStatus?: string, isHydratingRef?: boolean): boolean {
  return formStatus === 'fetching' || isHydratingRef === true;
}

function resolveBaselineSnapshot(input: Pick<DocumentStoreDirtySyncInput, 'baselineSnapshot' | 'storeKey'>): string | null {
  if (input.baselineSnapshot != null) {
    return input.baselineSnapshot;
  }
  if (input.storeKey != null) {
    return getDocumentStoreBaseline(input.storeKey);
  }
  return null;
}

/**
 * Capture the post-hydration store snapshot as the dirty baseline.
 * Call after form hydration completes — never on store mutations.
 */
export function establishDocumentStoreBaseline(
  input: Omit<DocumentStoreDirtySyncInput, 'baselineSnapshot'>,
): DocumentStoreBaselineResult {
  if (isFormHydrating(input.formStatus, input.isHydratingRef)) {
    return { established: false, freshCapture: false };
  }

  if (input.storeKey != null) {
    const existing = getDocumentStoreBaseline(input.storeKey);
    if (existing != null) {
      input.setBaselineSnapshot(existing);
      return { established: true, freshCapture: false };
    }
  }

  const snapshot = serializeDocumentListItems(input.items);
  if (input.storeKey != null) {
    storeBaselines.set(input.storeKey, snapshot);
  }
  input.setBaselineSnapshot(snapshot);
  return { established: true, freshCapture: true };
}

/**
 * Pure decision helper for document-store ↔ RHF dirty bridging.
 * Never establishes baseline — use establishDocumentStoreBaseline after hydration.
 */
export function evaluateDocumentStoreDirtySync(
  input: DocumentStoreDirtySyncInput,
): DocumentStoreDirtySyncResult {
  const { items, formStatus, isHydratingRef } = input;
  const snapshot = serializeDocumentListItems(items);
  const baselineSnapshot = resolveBaselineSnapshot(input);

  if (isFormHydrating(formStatus, isHydratingRef)) {
    return { shouldMarkDirty: false, resetDirtyField: false };
  }

  if (baselineSnapshot === null) {
    return { shouldMarkDirty: true, resetDirtyField: false };
  }

  const changed = snapshot !== baselineSnapshot;
  return { shouldMarkDirty: changed, resetDirtyField: !changed };
}
